import type { MisconceptionDefinition } from '../types';
export const seedAdditionsRonde2Misconceptions: MisconceptionDefinition[] = [
// ─── Seed-driven additions ronde 2 (mei 2026) ───────────────────────────
// Onderstaande tags worden actief gebruikt in seeds maar ontbraken in de
// registry, wat PEDAGOGY_MISCONCEPTION_UNKNOWN-blockers veroorzaakte.
// Per registry-policy (zie regel 142-147): seed-naming style geaccepteerd.

// --- Zijn/hebben varianten (SWW-TT-ZIJN-HEBBEN) ---
{
  id: 'sww.zijnhebben-verward-bezit',
  label: 'Zijn gebruikt bij bezit',
  module: 'SpellingWW',
  description: 'Leerling kiest een vorm van "zijn" waar "hebben" hoort, in zinnen die bezit uitdrukken (bv. "ik ben een auto" i.p.v. "ik heb een auto"). Begripsfout: zijn is voor identiteit/toestand, hebben voor bezit.',
  typicalFamilies: ['SWW-TT-ZIJN-HEBBEN']
}, {
  id: 'sww.zijnhebben-verward-gevoel',
  label: 'Hebben gebruikt bij gevoel/eigenschap',
  module: 'SpellingWW',
  description: 'Leerling kiest een vorm van "hebben" waar "zijn" hoort, in zinnen die een gevoel of eigenschap uitdrukken (bv. "ik heb blij" i.p.v. "ik ben blij"). Spiegelfout van zijnhebben-verward-bezit.',
  typicalFamilies: ['SWW-TT-ZIJN-HEBBEN']
}, {
  id: 'sww.zijnhebben-persoon-verward',
  label: 'Verkeerde persoonsvorm van zijn/hebben',
  module: 'SpellingWW',
  description: 'Leerling kiest de vorm bij de verkeerde persoon (bv. "hij heb" i.p.v. "hij heeft", of "hij is" → "hij ben"). Onregelmatige paradigma\'s van zijn/hebben worden niet correct uit het hoofd toegepast.',
  typicalFamilies: ['SWW-TT-ZIJN-HEBBEN']
}, {
  id: 'sww.zijnhebben-meervoud-bij-enkelvoud',
  label: 'Meervoudsvorm van zijn/hebben bij enkelvoudig onderwerp',
  module: 'SpellingWW',
  description: 'Leerling kiest "hebben" of "zijn" (meervoud) bij een enkelvoudig onderwerp (bv. "hij hebben" i.p.v. "hij heeft").',
  typicalFamilies: ['SWW-TT-ZIJN-HEBBEN']
}, {
  id: 'sww.zijnhebben-enkelvoud-meervoud',
  label: 'Enkelvoudsvorm van zijn/hebben bij meervoudig onderwerp',
  module: 'SpellingWW',
  description: 'Leerling kiest "heeft" of "is" (enkelvoud) bij een meervoudig onderwerp (bv. "wij heeft" i.p.v. "wij hebben"). Spiegelfout van zijnhebben-meervoud-bij-enkelvoud.',
  typicalFamilies: ['SWW-TT-ZIJN-HEBBEN', 'SWW-VT-ZIJN-HEBBEN']
},
// --- Inversie varianten (SWW-TT-INV-JIJ) ---
{
  id: 'sww.inversie-tijd-verward',
  label: 'Verkeerde tijd gekozen bij inversie',
  module: 'SpellingWW',
  description: 'Leerling kiest een verleden-tijdvorm in een tegenwoordige-tijdscontext (of andersom) bij een inversie-zin met jij. De inversie-regel zelf wordt mogelijk goed toegepast, maar op de verkeerde tijd.',
  typicalFamilies: ['SWW-TT-INV-JIJ']
}, {
  id: 'sww.inversie-vd-gebruikt',
  label: 'Voltooid deelwoord gebruikt bij inversie i.p.v. stam',
  module: 'SpellingWW',
  description: 'Leerling gebruikt een voltooid deelwoord (bv. "gelopen jij") bij inversie waar de tegenwoordige-tijdsstam ("loop jij") hoort.',
  typicalFamilies: ['SWW-TT-INV-JIJ']
},
// --- "Word/wordt" subject-checks (SWW-VD-HOMOFOON) ---
{
  id: 'sww.word-wordt-ik',
  label: 'Stam-zonder-t-regel niet toegepast bij ik',
  module: 'SpellingWW',
  description: 'Leerling schrijft "ik wordt" of "ik vindt" i.p.v. "ik word"/"ik vind". De regel "ik-vorm = kale stam zonder -t" wordt vergeten, vaak doordat de hij-vorm met -t overgeneraliseerd wordt.',
  typicalFamilies: ['SWW-VD-HOMOFOON', 'SWW-TT-DSTAM']
}, {
  id: 'sww.word-wordt-hij',
  label: 'Inversie genegeerd: -t weggelaten bij werkwoord-vóór-hij',
  module: 'SpellingWW',
  description: 'Leerling laat de -t weg bij "wordt hij?" of "vindt hij?", door de jij-inversieregel ten onrechte ook op hij/zij/het toe te passen. Bij hij/zij/het blijft de -t altijd staan, ook bij inversie.',
  typicalFamilies: ['SWW-VD-HOMOFOON', 'SWW-TT-INV-TRICK']
}, {
  id: 'sww.word-wordt-het',
  label: 'Onderwerp uit bijzin gekozen i.p.v. hoofdzin',
  module: 'SpellingWW',
  description: 'Leerling identificeert het verkeerde onderwerp bij een werkwoord (bv. neemt "ik" uit een bijzin als onderwerp van het hoofdwerkwoord), waardoor de persoonsvorm verkeerd vervoegd wordt. Begripsfout: onderwerp bepalen vóór vervoeging.',
  typicalFamilies: ['SWW-VD-HOMOFOON']
}, {
  id: 'sww.stam-plus-t-vergeten',
  label: 'Stam + t vergeten bij hij/zij/het',
  module: 'SpellingWW',
  description: 'Leerling vergeet de -t toe te voegen aan de stam bij 3e persoon enkelvoud (bv. "hij loop" i.p.v. "hij loopt"). Klassieke vergissing bij beginners; vaak na het leren van de ik-vorm (stam zonder -t).',
  typicalFamilies: ['SWW-VD-HOMOFOON', 'SWW-TT-STAM']
},
// --- TT-DSTAM seed tags ---
{
  id: 'sww.ik-vorm-met-t',
  label: 'Ik-vorm met -t geschreven',
  module: 'SpellingWW',
  description: 'Leerling voegt -t toe aan de ik-vorm (bv. "ik wordt" i.p.v. "ik word"), door de hij-vorm-regel te overgeneraliseren. Direct verwant aan sww.word-wordt-ik maar specifiek voor d-stam werkwoorden.',
  typicalFamilies: ['SWW-TT-DSTAM']
}, {
  id: 'sww.onregelmatig-misapplied',
  label: 'Onregelmatige paradigma verkeerd toegepast',
  module: 'SpellingWW',
  description: 'Leerling herziet of vervoegt een onregelmatig werkwoord (hebben, zijn, worden) volgens een verkeerd patroon, bv. door regels van een ander onregelmatig werkwoord toe te passen.',
  typicalFamilies: ['SWW-TT-DSTAM', 'SWW-VT-ONREGELMATIG']
},
// --- VD-STERK seed tags ---
{
  id: 'sww.vd-vt-vd-verwarring',
  label: 'VT-vorm en VD-vorm van sterk werkwoord verward',
  module: 'SpellingWW',
  description: 'Leerling verwart de verleden-tijdvorm (bv. "liep") met het voltooid deelwoord (bv. "gelopen") bij sterke werkwoorden. Beide hebben een klankverandering maar verschillen in vorm en gebruik (VT staat los, VD bij hulpwerkwoord).',
  typicalFamilies: ['SWW-VD-STERK']
},
// --- VD-KOF seed tags ---
{
  id: 'sww.kofschip-wrong-ending',
  label: 'Verkeerde kofschip-uitgang gekozen',
  module: 'SpellingWW',
  description: 'Leerling kiest -d waar -t hoort (of andersom) bij voltooid deelwoord, doordat de kofschip-check verkeerd uitgevoerd of in de verkeerde richting toegepast wordt. Concretere variant van sww.kofschip-misapplied.',
  typicalFamilies: ['SWW-VD-KOF', 'SWW-VT-ZWAK-KOF']
},
// --- Verwart/verward homofoon-paar (SWW-VD-HOMOFOON) — added juni 2026 ---
// Closes L-SWW-5 verwart/verward gap (Batch 3 VD audit P1). Het paar
// verwart (TT 3e p.) / verward (VD of bijvoeglijk) is een klassieke
// homofoon waar zowel onderwerp-detectie als werkwoord-rol-herkenning fout
// kan gaan.
{
  id: 'sww.verwart-verward-tt-vd',
  label: 'TT-vorm (verwart) gebruikt waar VD (verward) hoort',
  module: 'SpellingWW',
  description: 'Leerling kiest "verwart" (TT 3e persoon enkelvoud: stam + t) op een plek waar het voltooid deelwoord "verward" hoort (na hulpwerkwoord, of bijvoeglijk gebruik). Klassieke homofoon-fout: de leerling hoort dezelfde klank en kiest de meest frequente vorm zonder de werkwoord-rol te analyseren.',
  typicalFamilies: ['SWW-VD-HOMOFOON']
}, {
  id: 'sww.verwart-verward-vd-als-tt',
  label: 'VD-vorm (verward) gebruikt waar TT (verwart) hoort',
  module: 'SpellingWW',
  description: 'Leerling kiest "verward" op een plek waar de tegenwoordige-tijd-vorm "verwart" hoort (hij/zij/het + stam + t). Spiegelfout van sww.verwart-verward-tt-vd: leerling herkent niet dat het werkwoord hier persoonsvorm is en geen voltooid deelwoord.',
  typicalFamilies: ['SWW-VD-HOMOFOON']
}];